import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Fade from "@material-ui/core/Fade";
import Typography from "@material-ui/core/Typography";

import { userCompanySelector } from "src/store/system/selector";
import {
  getCachedImageAsBase64,
  prefetchImageAsBase64,
} from "src/utils/storage";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minHeight: 90, // Reserve space for logo (56px) + company name (~34px)
  },
  logoContainer: {
    height: 56, // Always reserve space for logo
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  logo: {
    height: 56,
  },
}));

export interface CompanyInfoProps {
  /** Print/export: no Fade (opacity transitions break window.print). */
  forPrint?: boolean;
}

function CompanyInfo({ forPrint = false }: CompanyInfoProps) {
  const classes = useStyles();
  const company = useSelector(userCompanySelector);
  const companyLogo = company?.logo;
  // undefined = fetching; null = no logo / error; string = base64
  const [logo, setLogo] = useState<string | null | undefined>(() => {
    if (!companyLogo) return null;
    const cached = getCachedImageAsBase64(companyLogo);
    return cached !== undefined ? cached : undefined;
  });

  useEffect(() => {
    let cancel = false;
    if (!companyLogo) {
      setLogo(null);
      return;
    }

    const cached = getCachedImageAsBase64(companyLogo);
    if (cached !== undefined) {
      setLogo(cached);
      return;
    }

    setLogo(undefined);
    prefetchImageAsBase64(companyLogo).then((base64) => {
      if (!cancel) setLogo(base64);
    });

    return () => {
      cancel = true;
    };
  }, [companyLogo]);

  if (!company) return null;

  const hasContent = !!(logo || company.name);

  const inner = (
    <div className={classes.root}>
      {companyLogo && (
        <div className={classes.logoContainer}>
          {logo &&
            (forPrint ? (
              <img
                className={classes.logo}
                src={logo}
                alt={company.name || "Company logo"}
              />
            ) : (
              <Fade in timeout={600}>
                <img
                  className={classes.logo}
                  src={logo}
                  alt={company.name || "Company logo"}
                />
              </Fade>
            ))}
        </div>
      )}
      {company.name ? (
        <Typography color="primary" variant="h6" component="p" align="center">
          {company.name}
        </Typography>
      ) : null}
    </div>
  );

  if (forPrint) {
    return inner;
  }

  return (
    <Fade in={hasContent} timeout={800}>
      {inner}
    </Fade>
  );
}

export default CompanyInfo;
